/**
 * RFC 5322 Message-IDs for References / In-Reply-To on outbound notification mail
 * (portal submit-bid failure/success emails via Resend).
 */

// Angle-bracketed ids only; blocks smuggled text outside tokens (defense-in-depth vs. raw header).
// NUL is injected via String.fromCharCode so the pattern is not a regex literal with control chars
// (eslint no-control-regex).
const NUL = String.fromCharCode(0);
const REFERENCES_MESSAGE_ID_RE = new RegExp(
  `<[^<>\\r\\n${NUL}]+@[^<>\\r\\n${NUL}]+>`,
  'g'
);

function hasCrlfOrNul(s: string): boolean {
  return s.includes('\r') || s.includes('\n') || s.includes(NUL);
}

// Normalize to a single angle-bracketed Message-ID.
export function normalizeRfcMessageId(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || hasCrlfOrNul(trimmed)) {
    return '';
  }

  const unwrapped = trimmed.replace(/^<|>$/g, '').trim();
  if (!unwrapped) {
    return '';
  }

  return `<${unwrapped}>`;
}

/**
 * Returns a space-separated References chain containing only extracted message-ids, or '' if unsafe
 * or empty. Rejects values with CR/LF/NUL (SMTP header-injection boundaries). Aligns with
 * drumkit's helpers.SanitizeEmailHeaderValue behavior for hostile headers.
 */
export function sanitizeReferencesHeaderForThreading(
  referencesHeader: string
): string {
  const trimmed = referencesHeader.trim();
  if (!trimmed || hasCrlfOrNul(trimmed)) {
    return '';
  }

  const rawMatches = trimmed.match(REFERENCES_MESSAGE_ID_RE) ?? [];
  const normalized = rawMatches
    .map((m) => normalizeRfcMessageId(m))
    .filter((s) => s.length > 0);

  return normalized.join(' ');
}

/**
 * Build References + In-Reply-To for a reply to the message currently open in read mode.
 * @param referencesHeader — parent message "References" (may be empty). sanitized via
 * {@link sanitizeReferencesHeaderForThreading}.
 */
export function buildRfcThreadingForOpenMessage(
  internetMessageId: string,
  referencesHeader: string
): { threadReferences: string; inReplyTo: string[] } {
  const normalizedId = normalizeRfcMessageId(internetMessageId);
  if (!normalizedId) {
    return { threadReferences: '', inReplyTo: [] };
  }

  const refsSanitized = sanitizeReferencesHeaderForThreading(referencesHeader);
  const threadReferences = refsSanitized
    ? `${refsSanitized} ${normalizedId}`.trim()
    : normalizedId;

  return {
    threadReferences,
    inReplyTo: [normalizedId],
  };
}
